import React, { useEffect, useEffectEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStoreStore } from '@/stores/storeStore';
import { useFunnelsData, useStoreData } from '@/queries/useStoreData';
import { QUERY_KEYS } from '@/lib/constants';
import { StoreType } from '@/types/store';
import { useUser } from '@/queries';
import { useAuthStore } from '@/stores';

interface StoreProviderProps {
    children: React.ReactNode;
    storeType?: StoreType;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children, storeType = 'linksite' }) => {
    const queryClient = useQueryClient();
    const { user } = useUser();
    const { storeInitialized } = useAuthStore();
    const storeId = user?.default_store_id;
    const addStore = useStoreStore(state => state.addStore);
    const clearStore = useStoreStore(state => state.clearStore);

    // Fetch store data when storeId is available
    const { data: storeData, isSuccess: storeSuccess, error: storeError } = useStoreData(storeId);
    // Only fetch funnels data after auth is initialized to prevent 401 errors
    const { data: funnels } = useFunnelsData(storeInitialized);

    const handleStoreUpdate = useEffectEvent((stores: any[]) => {
        for (const store of stores) {
            addStore(store);
        }
    });

    const handleClearStore = useEffectEvent(() => {
        clearStore();
    });

    const handleInvalidateQueries = useEffectEvent((id: number, type: StoreType) => {
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.STORE_DATA, id, type],
        });
    });

    useEffect(() => {
        if (storeSuccess && storeData && funnels) {
            const stores = [...storeData, ...funnels];
            handleStoreUpdate(stores);
            // TODO: Add this back in with themes
            // addThemeColorsToDocument();
        } else if (storeError) {
            handleClearStore();
        }
    }, [storeData, storeSuccess, funnels, storeError]);

    // Invalidate store data when storeId changes
    useEffect(() => {
        if (storeId) {
            handleInvalidateQueries(storeId, storeType);
        }
    }, [storeId, storeType]);

    return <>{children}</>;
};
